import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ShieldCheck, TrendingUp, Users, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = [
    "Insurance",
    "Travel",
    "Lifestyle",
    "Health & Wellness",
    "Finance",
    "Business",
    "Other",
];

const INITIAL = {
    name: "",
    company: "",
    role: "",
    email: "",
    phone: "",
    website: "",
    category: "",
    message: "",
};

export default function BecomeVendor() {
    const [form, setForm] = useState(INITIAL);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [err, setErr] = useState("");

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const payload = {
                form: "Vendor enquiry",
                submitted_at: new Date().toISOString(),
                ...form,
            };
            const { data } = await api.post("/webform/submit", payload);
            setDone(data);
            toast.success("Enquiry sent — we'll be in touch shortly.");
        } catch (e2) {
            const msg = formatApiError(e2);
            setErr(msg);
            toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <section className="bg-cream min-h-[70vh]">
                <div className="mx-auto max-w-3xl px-6 lg:px-10 py-24 text-center">
                    <div className="mx-auto h-20 w-20 rounded-full bg-gold-500/15 flex items-center justify-center mb-8">
                        <CheckCircle2 size={36} className="text-gold-600" />
                    </div>
                    <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                        Received
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl text-navy-700 mb-4">
                        Thanks — the flock has your enquiry.
                    </h1>
                    <p className="text-navy-500 text-lg leading-relaxed max-w-xl mx-auto mb-8">
                        A member of our vendor team will reach out within one business day
                        to discuss listing your product on Perx Group.
                    </p>
                    <p className="text-xs text-navy-500 mb-10">
                        Reference:{" "}
                        <code className="bg-white px-2 py-1 rounded border border-navy-100">
                            {done.message_id}
                        </code>
                    </p>
                    <Link to="/">
                        <Button className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-11 px-6">
                            Back to Perx Group
                        </Button>
                    </Link>
                </div>
            </section>
        );
    }

    return (
        <>
            <section className="bg-white">
                <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-[1fr_360px] items-center gap-10">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-3">
                            For vendors & partners
                        </div>
                        <h1 className="font-display text-5xl md:text-6xl text-navy-700 leading-[1.05] mb-5">
                            Become a Perx partner.
                        </h1>
                        <p className="text-navy-500 text-lg leading-relaxed max-w-2xl">
                            Reach an engaged members club with a taste for the good stuff.
                            Tell us about your product below and our vendor team will
                            reach out within one business day.
                        </p>
                    </div>
                    <img
                        src="/panda/panda-business.png"
                        alt=""
                        className="max-w-[300px] mx-auto object-contain"
                    />
                </div>
            </section>

            <section className="bg-cream py-16">
                <div className="mx-auto max-w-6xl px-6 lg:px-10 grid lg:grid-cols-[1fr_360px] gap-10 items-start">
                    <form
                        onSubmit={submit}
                        data-testid="vendor-enquiry-form"
                        className="bg-white rounded-2xl shadow-soft p-8 lg:p-10 space-y-5"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Full name" required>
                                <Input
                                    required
                                    data-testid="vendor-name"
                                    value={form.name}
                                    onChange={update("name")}
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Company">
                                <Input
                                    data-testid="vendor-company"
                                    value={form.company}
                                    onChange={update("company")}
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Role / title">
                                <Input
                                    data-testid="vendor-role"
                                    value={form.role}
                                    onChange={update("role")}
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Work email" required>
                                <Input
                                    type="email"
                                    required
                                    data-testid="vendor-email"
                                    value={form.email}
                                    onChange={update("email")}
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Phone">
                                <Input
                                    data-testid="vendor-phone"
                                    value={form.phone}
                                    onChange={update("phone")}
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                            <Field label="Website">
                                <Input
                                    data-testid="vendor-website"
                                    value={form.website}
                                    onChange={update("website")}
                                    placeholder="https://"
                                    className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500 h-11"
                                />
                            </Field>
                        </div>

                        <Field label="Category">
                            <Select value={form.category} onValueChange={update("category")}>
                                <SelectTrigger
                                    data-testid="vendor-category"
                                    className="mt-2 bg-white border-navy-100 h-11"
                                >
                                    <SelectValue placeholder="Which pocket best fits your product?" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {CATEGORIES.map((c) => (
                                        <SelectItem key={c} value={c}>
                                            {c}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>

                        <Field label="Tell us about your product" required>
                            <Textarea
                                required
                                data-testid="vendor-message"
                                value={form.message}
                                onChange={update("message")}
                                placeholder="Product, target audience, why it's a fit for Perx members…"
                                rows={5}
                                className="mt-2 bg-white border-navy-100 focus-visible:ring-gold-500"
                            />
                        </Field>

                        {err && (
                            <div
                                data-testid="vendor-error"
                                className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                            >
                                {err}
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-2">
                            <div className="text-xs text-navy-500 max-w-sm">
                                Your enquiry will be sent to{" "}
                                <strong className="text-navy-700">enquiry@perx.group</strong>{" "}
                                — we typically reply within one business day.
                            </div>
                            <Button
                                type="submit"
                                data-testid="vendor-submit"
                                disabled={busy}
                                className="rounded-full bg-navy-700 hover:bg-navy-800 text-white h-12 px-7 font-semibold"
                            >
                                {busy ? "Sending…" : (
                                    <>
                                        Send enquiry
                                        <ArrowRight size={16} className="ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>

                    <aside className="space-y-5 lg:sticky lg:top-8">
                        <VendorBenefit
                            icon={Users}
                            title="Engaged members"
                            body="Thousands of Boost members redeeming perks every month — real intent, real purchases."
                        />
                        <VendorBenefit
                            icon={TrendingUp}
                            title="Vendor-negotiated"
                            body="Our team helps craft an offer that beats your public price so members instantly see the value."
                        />
                        <VendorBenefit
                            icon={ShieldCheck}
                            title="Panda Promise"
                            body="30-day price guarantee backs every listing — reduces refunds and builds trust for your brand."
                        />
                    </aside>
                </div>
            </section>
        </>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            {children}
        </div>
    );
}

function VendorBenefit({ icon: Icon, title, body }) {
    return (
        <div className="bg-white rounded-2xl shadow-soft p-6">
            <Icon size={22} className="text-gold-600 mb-3" strokeWidth={1.7} />
            <div className="font-display text-lg text-navy-700 mb-1">{title}</div>
            <p className="text-navy-500 text-sm leading-relaxed">{body}</p>
        </div>
    );
}
